/**
 * ChatFlow Zustand Store
 * Scope: exclusive to /flow
 * React Flow nodes/edges live in local hooks (@xyflow/react requirement).
 */
import { create } from 'zustand'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FlowData = Record<string, any> | null;

interface FlowStore {
  // ── Flow metadata ──
  flowId: string | null;
  flow: FlowData;
  loading: boolean;
  saving: boolean;
  dirty: boolean;

  // ── UI state ──
  selectedNode: FlowData;
  contextMenu: FlowData;
  toast: { message: string; type: string } | null;

  // —— Confirm dialog (replaces window.confirm) ——
  confirmDialog: { message: string; onConfirm?: () => void; onCancel?: () => void } | null;

  // ── Actions: flow ──
  setFlowId: (flowId: string) => void;
  setFlow: (flow: FlowData) => void;
  updateFlow: (updates: FlowData) => void;
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setDirty: (dirty: boolean) => void;

  // ── Actions: UI ──
  setSelectedNode: (selectedNode: FlowData) => void;
  setContextMenu: (contextMenu: FlowData) => void;

  // ── Actions: toast ──
  showToast: (message: string, type?: string) => void;
  dismissToast: () => void;

  // ── Actions: confirm dialog ──
  showConfirm: (message: string, onConfirm?: () => void, onCancel?: () => void) => void;
  hideConfirm: () => void;
}

const useFlowStore = create<FlowStore>((set) => ({
  // ── Flow metadata ──
  flowId: null,
  flow: null,
  loading: true,
  saving: false,
  dirty: false,

  // ── UI state ──
  selectedNode: null,
  contextMenu: null,
  toast: null,

  // —— Confirm dialog (replaces window.confirm) ——
  confirmDialog: null,

  // ── Actions: flow ──
  setFlowId: (flowId) => set({ flowId }),
  setFlow: (flow) => set({ flow }),
  updateFlow: (updates) =>
    set((state) => ({ flow: { ...state.flow, ...updates }, dirty: true })),
  setLoading: (loading) => set({ loading }),
  setSaving: (saving) => set({ saving }),
  setDirty: (dirty) => set({ dirty }),

  // ── Actions: UI ──
  setSelectedNode: (selectedNode) => set({ selectedNode, contextMenu: null }),
  setContextMenu: (contextMenu) => set({ contextMenu }),

  // ── Actions: toast ──
  showToast: (message, type = 'success') => {
    set({ toast: { message, type } })
    setTimeout(() => set({ toast: null }), 3000)
  },
  dismissToast: () => set({ toast: null }),

  // ── Actions: confirm dialog ──
  showConfirm: (message, onConfirm, onCancel) =>
    set({ confirmDialog: { message, onConfirm, onCancel } }),
  hideConfirm: () => set({ confirmDialog: null }),
}))

export default useFlowStore
